import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  Briefcase,
  Salad,
  ShieldCheck,
  Leaf,
  Users,
  Clock,
  UtensilsCrossed,
  Package,
  FileText,
} from "lucide-react";
import { useSeo } from "@/components/Seo";

const HERO_IMAGE = "/api/images/5753105c-176d-44e4-8a6d-b6ec55bafe98";

const EVENT_TYPES = [
  {
    title: "Office meetings & lunches",
    text: "Working lunches, client meetings and boardroom sessions with menus sized for the headcount — served on schedule so the meeting stays on track.",
  },
  {
    title: "Corporate celebrations",
    text: "Annual days, festive gatherings and milestone celebrations at the office or a venue, with starters, mains, live counters and desserts arranged around your programme.",
  },
  {
    title: "Product launches",
    text: "Catering for launch events and client-facing functions, planned around the event flow so food service complements the presentation rather than interrupting it.",
  },
  {
    title: "Team events",
    text: "Team lunches, offsites and appreciation events with flexible menus — from simple, quick-serve meals to fuller spreads for larger groups.",
  },
];

const WHY = [
  { icon: Salad, title: "Vegetarian focus", text: "A pure-vegetarian kitchen with a menu built around vegetarian starters, mains, live counters and sweets." },
  { icon: UtensilsCrossed, title: "130+ dishes to choose from", text: "Plan each event's menu from a large, real menu — not a fixed corporate thali." },
  { icon: ShieldCheck, title: "Hygienic & safe", text: "Food prepared in a hygienic kitchen with fresh ingredients." },
  { icon: Users, title: "Experienced team", text: "A team that has served events for offices, families and large gatherings across Mumbai." },
  { icon: Clock, title: "On-time service", text: "Service planned around your meeting and event timings — punctuality matters in a corporate setting." },
  { icon: Leaf, title: "Transparent quotations", text: "You get a written, itemised quotation to review and approve before anything is confirmed." },
];

export default function CorporateCateringMumbai() {
  useSeo({
    title: "Corporate Catering in Mumbai | Vegetarian Caterers | Tezz Khana",
    description:
      "Vegetarian corporate catering in Mumbai by Tezz Khana for office events, meetings and business celebrations. Flexible menus across Mumbai, Navi Mumbai and Thane. Request a quote.",
    path: "/corporate-catering-mumbai",
    ogTitle: "Corporate Catering in Mumbai | Tezz Khana",
    ogDescription:
      "Vegetarian corporate catering in Mumbai for office events, meetings and business celebrations, with flexible menus from Tezz Khana.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-corporate-catering";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Corporate catering",
      name: "Corporate Catering in Mumbai",
      description:
        "Vegetarian corporate catering in Mumbai for office meetings, corporate celebrations, product launches and team events, with flexible menus and custom quotations.",
      url: "https://tezzkhana.com/corporate-catering-mumbai",
      areaServed: [
        { "@type": "City", name: "Mumbai" },
        { "@type": "City", name: "Navi Mumbai" },
        { "@type": "City", name: "Thane" },
      ],
      provider: {
        "@type": "FoodEstablishment",
        name: "Tezz Khana Catering Service",
        url: "https://tezzkhana.com",
        servesCuisine: "Indian vegetarian",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Goregaon East, Mumbai",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-corporate-catering")?.remove();
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="container-x py-14 sm:py-20" data-testid="ccm-hero">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="kbd-eyebrow">
              <Briefcase className="h-3 w-3" /> Tezz Khana Catering Service
            </span>
            <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight text-secondary sm:text-5xl lg:text-6xl">
              Corporate Catering in Mumbai
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Reliable, professional vegetarian catering for office events, business
              meetings and corporate celebrations — thoughtfully prepared and delivered
              on time, every time.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-quote"
                data-testid="ccm-hero-quote"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Request a Custom Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/build-your-menu"
                data-testid="ccm-hero-build"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border px-6 text-sm font-semibold text-secondary hover:bg-muted"
              >
                <ChefHat className="h-4 w-4" /> Build Your Menu
              </Link>
            </div>
          </div>
          <img
            src={HERO_IMAGE}
            alt="Hara bhara kabab starters from the Tezz Khana vegetarian corporate menu"
            className="h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80 lg:h-96"
            loading="eager"
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="container-x pb-14" data-testid="ccm-intro">
        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          <p>
            Tezz Khana is a vegetarian catering service based in Goregaon East, providing
            corporate catering across Mumbai. Whether it&apos;s a working lunch for a small
            team or a company-wide celebration, the catering is planned around your
            meeting size, event type and dietary preferences.
          </p>
          <p>
            There is no fixed corporate menu — browse the{" "}
            <Link to="/menu" className="font-semibold text-primary hover:underline">
              full menu
            </Link>
            , start from ready-made{" "}
            <Link to="/packages" className="font-semibold text-primary hover:underline">
              packages
            </Link>{" "}
            or build your own selection, and then request a customised quotation for your
            event.
          </p>
        </div>
      </section>

      {/* CORPORATE EVENT TYPES */}
      <section className="container-x pb-16" data-testid="ccm-event-types">
        <span className="kbd-eyebrow">Corporate catering options</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Corporate event types we serve
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {EVENT_TYPES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <h3 className="text-sm font-semibold text-secondary">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MENU & FOOD OPTIONS */}
      <section className="container-x pb-16" data-testid="ccm-menu">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Menu & food options</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Choose from 130+ vegetarian dishes
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            The Tezz Khana menu covers what an office event needs: welcome drinks, fresh
            juices and mocktails, vegetarian starters, soups and salads, veg and paneer
            main courses, live counters for pasta and chaat, and desserts and sweets. Pick
            dishes that suit the occasion — a quick lunch, a formal client meal or a
            festive spread.
          </p>
          <Link
            to="/menu"
            data-testid="ccm-menu-link"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Explore the full menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* PACKAGES + BUILD YOUR MENU */}
      <section className="container-x grid gap-6 pb-16 lg:grid-cols-2" data-testid="ccm-packages">
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Flexible packages</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            If you&apos;d rather start from a ready structure, Tezz Khana offers Silver,
            Gold and Platinum packages, plus a fully custom option. Each package can be
            adjusted to your event and headcount before you receive a quotation.
          </p>
          <Link
            to="/packages"
            data-testid="ccm-packages-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <ChefHat className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Build your event menu</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Prefer to hand-pick every dish? Use the menu builder to select starters,
            mains, live counters and sweets dish by dish, exactly as you want them served
            at your event.
          </p>
          <Link
            to="/build-your-menu"
            data-testid="ccm-build-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Start building your menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="container-x pb-16" data-testid="ccm-why">
        <span className="kbd-eyebrow">Why choose Tezz Khana</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Why offices choose Tezz Khana for corporate events
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <w.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-sm font-semibold text-secondary">{w.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="container-x pb-16" data-testid="ccm-service-area">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service area</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Serving offices across Mumbai, Navi Mumbai and Thane
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Based in Goregaon East, Tezz Khana handles corporate catering across Mumbai —
            from South Mumbai business districts to the Western and Central suburbs — as
            well as Navi Mumbai and Thane. Share your office or venue location while
            requesting your quote and we&apos;ll plan the service around it.
          </p>
        </div>
      </section>

      {/* HOW TO REQUEST A QUOTE */}
      <section className="container-x pb-16" data-testid="ccm-steps">
        <span className="kbd-eyebrow">How it works</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          How to request a corporate catering quote
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Choose your event requirements", d: "Tell us the event type, date and expected headcount." },
            { n: "2", t: "Select or customise your menu", d: "Pick a package or build your own dish-by-dish selection." },
            { n: "3", t: "Request a quotation", d: "Receive an itemised quote to review, adjust and approve." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{s.n}</span>
              <h3 className="mt-3 text-sm font-semibold text-secondary">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <Link
          to="/request-quote"
          data-testid="ccm-steps-quote-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <FileText className="h-4 w-4" /> Go to the quote request form <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="ccm-cta">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
              Planning a Corporate Event in Mumbai?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-primary-foreground/80">
              Tell us about your event and catering requirements, and we&apos;ll help you
              plan a suitable menu.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/request-quote"
              data-testid="ccm-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/build-your-menu"
              data-testid="ccm-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
